import { Badge } from "@designs-by-maryam/ui/components/badge";
import { Button } from "@designs-by-maryam/ui/components/button";
import { Input } from "@designs-by-maryam/ui/components/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@designs-by-maryam/ui/components/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@designs-by-maryam/ui/components/tooltip";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Mail, Search, UserPlus, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { requireAdmin } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/admin/users")({
	component: AdminUsers,
	beforeLoad: async () => {
		await requireAdmin();
	},
});

type User = {
	id: string;
	name?: string | null;
	email: string;
	role?: string | null;
	emailVerified?: boolean | null;
	createdAt?: string | null;
};

function getRoleBadgeVariant(role?: string | null) {
	if (role === "admin") {
		return "default";
	}
	if (role === "designer") {
		return "secondary";
	}
	return "outline";
}

function AdminUsers() {
	const [searchQuery, setSearchQuery] = useState("");
	const listUsers = useQuery(orpc.listAllUsers.queryOptions());

	// Filter users based on search query
	const filteredUsers = useMemo(() => {
		const users = listUsers.data?.users ?? [];
		if (!searchQuery.trim()) {
			return users;
		}
		const query = searchQuery.toLowerCase();
		return users.filter(
			(user: User) =>
				user.name?.toLowerCase().includes(query) ||
				user.email.toLowerCase().includes(query) ||
				user.role?.toLowerCase().includes(query),
		);
	}, [listUsers.data?.users, searchQuery]);

	return (
		<div className="container mx-auto p-6">
			<div className="mb-8">
				<h1 className="flex items-center gap-3 font-bold text-3xl">
					<Users className="h-8 w-8 text-primary" />
					User Management
				</h1>
				<p className="mt-2 text-muted-foreground">
					View and manage all users in the system
				</p>
			</div>

			<div className="mb-6 flex gap-4">
				<div className="relative flex-1">
					<Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
					<Input
						aria-label="Search users by name, email, or role"
						className="pl-10"
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search users by name, email, or role..."
						type="search"
						value={searchQuery}
					/>
				</div>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button disabled variant="outline">
								<UserPlus className="mr-2 h-4 w-4" />
								Add User
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<p>Users are created via email invitations</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>

			<div className="rounded-lg border bg-card">
				{listUsers.isLoading && (
					<div className="p-6">
						<p className="text-muted-foreground">Loading users...</p>
					</div>
				)}

				{listUsers.error && (
					<div className="border-destructive bg-destructive/10 p-6">
						<p className="font-medium text-destructive">Error loading users</p>
						<p className="mt-1 text-muted-foreground text-sm">
							{listUsers.error.message}
						</p>
					</div>
				)}

				{listUsers.data &&
					(filteredUsers.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Email</TableHead>
									<TableHead>Role</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Joined</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredUsers.map((user: User) => (
									<TableRow key={user.id}>
										<TableCell className="font-medium">
											{user.name || "—"}
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Mail className="h-4 w-4 text-muted-foreground" />
												{user.email}
											</div>
										</TableCell>
										<TableCell>
											<Badge variant={getRoleBadgeVariant(user.role)}>
												{user.role || "user"}
											</Badge>
										</TableCell>
										<TableCell>
											<Badge
												variant={user.emailVerified ? "default" : "outline"}
											>
												{user.emailVerified ? "Verified" : "Pending"}
											</Badge>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{user.createdAt
												? new Date(user.createdAt).toLocaleDateString()
												: "—"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="p-6">
							<p className="py-8 text-center text-muted-foreground">
								{searchQuery
									? `No users found matching "${searchQuery}"`
									: "No users found. Invite users via the invitation system."}
							</p>
						</div>
					))}
			</div>

			{/* User count */}
			{listUsers.data && (
				<p className="mt-4 text-muted-foreground text-sm">
					{filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}
					{searchQuery && ` matching "${searchQuery}"`}
					{listUsers.data.users &&
						filteredUsers.length !== listUsers.data.users.length &&
						` of ${listUsers.data.users.length} total`}
				</p>
			)}
		</div>
	);
}
